/**
 * Whether a backend-authored colour is safe to place in an inline `style` value.
 *
 * Same posture as [safe-link]: the backend validates on write, but a preview
 * renders form values it has never seen, so the surface checks anyway.
 */

/** `#rgb`, `#rrggbb`, `#rrggbbaa`. */
const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i

/**
 * `rgb()` / `rgba()` with plain decimal components, each channel held to 0-255.
 *
 * Two deliberate narrowings, both so a second validator cannot quietly disagree
 * with this one — the backend gets its own copy of this rule the moment colours
 * become operator-authored:
 *
 * - the separators are `[ \t]` rather than `\s`, which in an un-flagged JS
 *   pattern also matches the BOM, NBSP, U+2028/U+2029 and other exotic space —
 *   none of which this allow-list promises;
 * - the channels are range-bounded rather than any three digits, so `rgb(999,0,0)`
 *   is refused here instead of being accepted and then rejected on write.
 */
const RGB_CHANNEL = String.raw`(?:25[0-5]|2[0-4]\d|1?\d?\d)`
const RGB_GAP = String.raw`[ \t]*`
const RGB_ALPHA = String.raw`(?:0|1(?:\.0+)?|0?\.\d+)`

const RGB_COLOR = new RegExp(
  `^rgba?\\(${RGB_GAP}${RGB_CHANNEL}${RGB_GAP},${RGB_GAP}${RGB_CHANNEL}${RGB_GAP},` +
    `${RGB_GAP}${RGB_CHANNEL}${RGB_GAP}(?:,${RGB_GAP}${RGB_ALPHA}${RGB_GAP})?\\)$`,
  'i',
)

/**
 * Everything else is rejected — `var()`, `url()`, colour functions, named
 * colours. Both patterns are anchored, so a value carrying a `;` that would
 * close the declaration and open another one cannot match either.
 */
export function isSafeAnnouncementColor(color: string): boolean {
  return HEX_COLOR.test(color) || RGB_COLOR.test(color)
}
